import type {
  ChartComponentSpec,
  ChartCustomRefLine,
  ChartSeriesDef,
  ComponentSpec,
  MetricOption,
} from "./types";
import { resolveSeriesMisc } from "./seriesMisc";

/** Baselines the backend stores as dedicated Players columns (api/consts.py
 *  PLAYER_BASELINE plus bodyweight_baseline). Every other baseline sits in the
 *  player.baselines JSON field, keyed by the metric's field key. */
const COLUMN_BASELINE_KEYS = new Set([
  "top_speed",
  "trimp",
  "trimp_per_minute",
  "max_hr",
  "bodyweight_baseline",
]);

function playerBaseline(player: any, metricKey: string): number {
  const raw = COLUMN_BASELINE_KEYS.has(metricKey)
    ? player?.[metricKey]
    : player?.baselines?.[metricKey];
  return parseFloat(raw);
}

/** Mirrors DashboardEngine._resolve_baseline_players: full team (or no
 *  selection) → the active roster; an explicitly selected player counts even
 *  when retired; position / group members must be active. Selections OR
 *  together. */
function subjectPlayers(subjects: string[], allPlayers: any[]): any[] {
  const players = allPlayers ?? [];
  const active = players.filter((p: any) => p.retired === false);
  if (subjects.length === 0 || subjects.includes("full-team")) return active;

  const picked = new Map<unknown, any>();
  for (const subject of subjects) {
    if (subject.startsWith("player-")) {
      const id = subject.slice("player-".length);
      const player = players.find((p: any) => String(p.player_id) === id);
      if (player) picked.set(player.player_id, player);
    } else if (subject.startsWith("pos-")) {
      const id = subject.slice("pos-".length);
      for (const p of active) {
        if (String(p.position_id) === id) picked.set(p.player_id, p);
      }
    } else if (subject.startsWith("group-")) {
      const id = Number(subject.slice("group-".length));
      for (const p of active) {
        if ((p.player_groups ?? []).includes(id)) picked.set(p.player_id, p);
      }
    }
  }
  return [...picked.values()];
}

function findSeriesForLine(
  line: ChartCustomRefLine,
  series: ChartSeriesDef[],
  metrics: MetricOption[],
): ChartSeriesDef | undefined {
  if (line.queryId) {
    const match = series.find((s) => s.queryId === line.queryId);
    if (match) return match;
  }
  // Legacy lines predate the queryId stamp — fall back to a metric match.
  if (line.metricKey) {
    const metric = metrics.find((m) => m.metricKey === line.metricKey);
    if (metric) {
      return series.find(
        (s) => s.series_builder_misc.selectedMetricId === metric.id,
      );
    }
  }
  return undefined;
}

/** The subjects the data query reads for this series. */
function effectiveSubjects(
  series: ChartSeriesDef | undefined,
  allSeries: ChartSeriesDef[],
): string[] {
  return resolveSeriesMisc(series, allSeries).subject?.selectedSubjects ?? [];
}

function resolveLine(
  line: ChartCustomRefLine,
  spec: ChartComponentSpec,
  allPlayers: any[],
  metrics: MetricOption[],
): ChartCustomRefLine | null {
  if (!line.metricKey) return null;
  const series = findSeriesForLine(line, spec.series, metrics);
  const subjects = effectiveSubjects(series, spec.series);
  const values = subjectPlayers(subjects, allPlayers)
    .map((p) => playerBaseline(p, line.metricKey!))
    .filter((v) => !isNaN(v));
  if (values.length === 0) return null;
  const value =
    Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100;
  return { ...line, value };
}

/**
 * Resolve every computeFromBaseline reference line in a chart spec to a value.
 *
 * The value comes from the owning plot's subject scope: one athlete → that
 * athlete's stored baseline, an aggregate scope → the mean of the members'
 * baselines. A line whose subjects have no stored baseline drops out.
 */
export function resolveSpecBaselines(
  spec: ComponentSpec,
  allPlayers: any[],
  metrics: MetricOption[],
): ComponentSpec {
  if (spec.type !== "chart") return spec;
  const lines = spec.widgetMutations?.customRefLines;
  if (!lines?.some((l) => l.computeFromBaseline)) return spec;

  return {
    ...spec,
    widgetMutations: {
      ...spec.widgetMutations,
      customRefLines: lines
        .map((l) =>
          l.computeFromBaseline ? resolveLine(l, spec, allPlayers, metrics) : l,
        )
        .filter(Boolean) as ChartCustomRefLine[],
    },
  };
}
